<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link"
                        class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom
                        title="Agregar Pago"
                        aria-label="Agregar Pago"
                        @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 190px; max-width: 260px;">
              <label class="filter-label">Punto de Venta</label>
              <v-select :options="arr.filters.points_sales" v-model="filters.points_sales" placeholder="Todos" :multiple="false" :select-on-tab="true"></v-select>
            </div>
            <div class="list-view-filter" style="flex: 1 1 230px; max-width: 340px;">
              <label class="filter-label">Proveedores</label>
              <v-select :options="arr.filters.suppliers" v-model="filters.suppliers" placeholder="Todos" :multiple="true" :select-on-tab="true"></v-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterPayments()" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-row>
        <b-col lg="12">
          <b-card>
            <b-row>
              <b-col sm="12">
                <b-table class="app-table"
                        ref="table"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        sticky-header="60vh"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="table.items"
                        :fields="table.fields"
                        :current-page="table.currentPage"
                        :per-page="table.perPage"
                        :tbody-tr-class="rowClass"
                        @row-clicked="onRowClicked"
                        :busy="table.isBusy"
                        v-if="table.items.length || table.isBusy">
                    
                    <template v-slot:table-colgroup="scope">
                      <col
                        v-for="field in scope.fields"                    
                        :key="field.key"
                        :style="{ width: field.width }"
                      >
                    </template> 

                    <template v-slot:table-busy>
                      <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Cargando...</strong>
                      </div>
                    </template>

                    <template v-slot:cell(business)="data">
                      {{data.item.point_sale.business.reference}}
                    </template>

                    <template v-slot:cell(id)="data">
                      <div v-if="data.item.type_voucher && data.item.point_sale">                        
                        {{data.item.type_voucher.name}} <br>
                        {{data.item.point_sale.point_sale.toString().padStart(4,'0') + '-' + data.item.number.toString().padStart(8,'0')}}
                      </div>
                    </template>
                    
                    <template v-slot:cell(date)="data">
                      {{moment(data.item.date).format('DD/MM/YYYY')}}
                    </template>

                    <template v-slot:cell(staff_id)="data">
                      <span v-if="data.item.staff_id" v-b-tooltip.hover :title="'STAFF: ' + data.item.staff.name">
                        <b-avatar :src="data.item.staff.image"
                                  v-if="data.item.staff.image">
                        </b-avatar>
                        <b-avatar v-else
                                  :text="avatarFromName(data.item.staff.name).text"
                                  :style="avatarFromName(data.item.staff.name).style">
                        </b-avatar>
                        <span v-if="isMobileViewport" class="ml-2">{{data.item.staff.name}}</span>
                      </span>
                    </template>

                    <template v-slot:cell(suppliers_id)="data">
                      <div v-if="data.item.supplier">
                        <b-avatar :src="data.item.supplier.image"
                                  v-if="data.item.supplier.image">
                        </b-avatar>
                        <b-avatar v-else
                                  :text="avatarFromName(data.item.supplier.name).text"
                                  :style="avatarFromName(data.item.supplier.name).style">
                        </b-avatar>
                        &nbsp;<b>{{data.item.supplier.name}}</b>

                        <AppIcon name="circle-fill" v-if="!data.item.supplier.active" class="ml-2 text-danger" v-b-tooltip.hover title="Proveedor INACTIVO" />
                      </div>
                    </template>

                    <template v-slot:cell(observations)="data">
                      <div v-if="data.item.observations">
                        <span v-if="isMobileViewport">{{data.item.observations}}</span>
                        <span v-else v-b-tooltip.hover :title="data.item.observations">
                          <AppIcon name="chat-square-text" />
                        </span>
                      </div>
                    </template>

                    <template v-slot:cell(amount_total)="data">
                      {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(data.item.amount_total)}}
                    </template>

                    <template v-slot:cell(amount_pending)="data">
                      <div v-if="data.item.amount_pending>0">
                        {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(data.item.amount_pending)}}
                      </div>
                    </template>

                    <template v-slot:cell(f_action)="data">
                      <b-dropdown right
                                  no-caret
                                  size="sm"
                                  variant="link"
                                  boundary="window"
                                  toggle-class="app-table-action-toggle"
                                  class="pull-right">
                        <template #button-content>
                          <AppIcon name="more_vert" label="Acciones" />
                        </template>

                        <b-dropdown-item @click="openSub(data.item)">
                          <AppIcon name="visibility" /> Visualizar
                        </b-dropdown-item>
                        <b-dropdown-item @click="sendPrinterSale(data.item)">
                          <AppIcon name="print" /> Imprimir
                        </b-dropdown-item>
                        <b-dropdown-item @click="addImputation(data.item)" v-if="data.item.amount_pending>0">
                          <AppIcon name="paperclip" /> Imputar
                        </b-dropdown-item>
                        <b-dropdown-header v-if="access.elements.deletePayments">Acciones</b-dropdown-header>
                        <b-dropdown-item @click="remove(data.item)" v-if="access.elements.deletePayments">
                          <AppIcon name="delete" class="text-danger" /> Eliminar
                        </b-dropdown-item>
                      </b-dropdown>
                    </template>
                </b-table>
                <EmptyState v-else icon="reports" title="No se encontraron registros" />

                <div class="app-table-footer">
                  <span class="list-view-toolbar__count" v-if="table.tablePaginate">
                    {{ table.tablePaginate.total }} registros
                  </span>
                  <export-excel
                      class="list-view-link"
                      :data="arr.export"
                      worksheet="Listado de Pagos"
                      name="list-payments.xls">
                      <AppIcon name="download" class="mr-1" /> Exportar
                  </export-excel>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- DRAWER: detalle del comprobante seleccionado -->
      <b-sidebar
          id="sidebar-payment-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle del Pago"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                {{itemSelected.type_voucher.name}} {{itemSelected.point_sale.point_sale.toString().padStart(4,'0') + '-' + itemSelected.number.toString().padStart(8,'0')}}
              </span>
              <div v-if="itemSelected.supplier">
                {{itemSelected.supplier.name}}
              </div>
              <div class="list-view-drawer__meta" v-if="itemSelected.date">
                {{moment(itemSelected.date).format('DD/MM/YYYY')}}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrevPayment"
                        v-b-tooltip.hover
                        title="Pago anterior"
                        @click="goPrevPayment()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNextPayment"
                        v-b-tooltip.hover
                        title="Pago siguiente"
                        @click="goNextPayment()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Total</span>
              <span class="list-view-drawer__stat-value">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(itemSelected.amount_total)}}
              </span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': itemSelected.amount_pending > 0 }">
              <span class="list-view-drawer__stat-label">Pendiente</span>
              <span class="list-view-drawer__stat-value">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(itemSelected.amount_pending || 0)}}
              </span>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="money" /> Medios de pago
            </div>
            <b-table  class="app-table app-table--compact table-payments-sub"
                      responsive
                      :stacked="isMobileViewport ? 'md' : false"
                      :hover="true"
                      :small="true"
                      :fixed="true"
                      :items="tableSubPayment.items"
                      :fields="tableSubPayment.fields"
                      v-if="tableSubPayment.items.length">

              <template v-slot:table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width }"
                >
              </template>

              <template v-slot:cell(medio)="row">
                <div v-if="row.item.type=='CASH'" class="text-truncate" v-b-tooltip.hover :title="row.item.relation.cash.name">
                  {{row.item.relation.cash.name}}
                </div>
                <div v-if="row.item.type=='CHEQUE'">
                  CHEQUE
                </div>
              </template>

              <template v-slot:cell(amount)="row">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(row.item.amount_total)}}
              </template>

            </b-table>
            <EmptyState v-else icon="money" title="Sin medios de pago" />
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="paperclip" /> Comprobantes imputados
            </div>
            <b-table  class="app-table app-table--compact table-payments-sub"
                      responsive
                      :stacked="isMobileViewport ? 'md' : false"
                      :hover="true"
                      :small="true"
                      :fixed="true"
                      :items="tableSubImputation.items"
                      :fields="tableSubImputation.fields"
                      v-if="tableSubImputation.items.length">

              <template v-slot:table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width }"
                >
              </template>

              <template v-slot:cell(voucher)="row">
                <div class="text-truncate" v-b-tooltip.hover :title="row.item.purchase.type_voucher.name + ' ' + row.item.purchase.point_sale.toString().padStart(4,'0') + '-' + row.item.purchase.number.toString().padStart(8,'0')">
                  {{row.item.purchase.type_voucher.name + ' ' + row.item.purchase.point_sale.toString().padStart(4,'0') + '-' + row.item.purchase.number.toString().padStart(8,'0')}}
                </div>
              </template>

              <template v-slot:cell(amount)="row">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(row.item.amount_total)}}
              </template>

              <template v-slot:cell(f_action)="row">
                <b-button size="sm"
                          variant="link"
                          class="app-table-icon-btn app-table-icon-btn--danger"
                          :class="{ 'app-table-icon-btn--labeled': isMobileViewport }"
                          v-b-tooltip.hover.left
                          title="Desimputar comprobante"
                          @click="saveDesimputar(row.item)">
                  <AppIcon name="link_off" label="Desimputar comprobante" />
                  <span v-if="isMobileViewport" class="ml-1">Desimputar</span>
                </b-button>
              </template>
            </b-table>
            <EmptyState v-else icon="paperclip" title="Sin imputaciones" />
          </div>

          <b-button v-if="tableSubPayment.items.length || tableSubImputation.items.length"
                    block
                    size="sm"
                    variant="outline-secondary"
                    class="text-left"
                    @click="openDetail()">
            <AppIcon name="visibility" class="mr-1" /> Ver detalle completo
          </b-button>

          <!-- En mobile no hay kebab en la fila (ver loadFieldTable): las
               mismas acciones quedan acá, ya desplegadas, sin menú. En
               desktop el kebab de la fila sigue existiendo, pero acceder a
               estas acciones también desde el propio drawer evita volver a
               la tabla — quedan visibles en los dos anchos. -->
          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="sendPrinterSale(itemSelected)">
              <AppIcon name="print" class="mr-1" /> Imprimir
            </b-button>
            <b-button size="sm" variant="outline-secondary" v-if="itemSelected.amount_pending>0" @click="addImputation(itemSelected)">
              <AppIcon name="paperclip" class="mr-1" /> Imputar
            </b-button>
          </div>
        </div>

        <!-- Eliminar: siempre al fondo del sidebar, aparte del contenido
             scrolleable — la acción destructiva no compite con el resto. -->
        <template #footer>
          <div class="list-view-drawer__footer" v-if="itemSelected && access.elements.deletePayments">
            <b-button size="sm" variant="outline-danger" block @click="remove(itemSelected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-row>
        <b-col lg="12">
          <b-card body-class="py-2">
            <div class="list-view-footer">
              <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
                <AppIcon name="back" />
                Volver
              </b-button>

              <nav v-if="table.tablePaginate">
                <b-pagination class="mb-0"
                              size="sm"
                              pills=""
                              :total-rows="table.tablePaginate.total"
                              :per-page="table.tablePaginate.per_page"
                              v-model="table.currentPage"
                              @input="filterPayments()" />
              </nav>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!--
      ###########################
      ######### PRINTER #########
      ###########################
      -->
      <Printer v-if="crud.print.id" 
              :reference="crud.print.reference" 
              :registerID="crud.print.id"
              :key="crud.print.key"/>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD MAIN -->
      <b-modal v-model="modal.form.active"
              no-close-on-esc
              no-close-on-backdrop
              hide-header-close
              size="xl"
              :key="modal.form._id"
              :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="edit" /></span>
          <h5 class="app-modal-header__title">{{this.modal.form.title}}</h5>
        </div>

        <form-wizard  title=""
                      subtitle=""
                      nextButtonText="Siguiente"
                      backButtonText="Atrás"
                      finishButtonText="Guardar"
                      color="#2f353a"
                      shape="tab"
                      @on-complete="save"
                      class="crud-payments-wizard">

          <tab-content title="General" icon="fa fa-edit" :before-change="paymentsValidGeneral">          
            <b-row>      
              <b-col md="4">
                <b-form-group label="Fecha">
                  <b-form-datepicker size="sm" v-model="crud.form.date" placeholder="Fecha" local="es"></b-form-datepicker>                      
                </b-form-group>            
              </b-col>
            </b-row>              
            <b-row> 
              <b-col md="5">
                <FindObject render="search"
                            type="suppliers" 
                            @select-object="loadSuppliers($event)" 
                            :valueID="crud.form.suppliers_id"
                            :where="conditionSuppliersActive"
                            :disabled="crud.form.id>0" />                 
              </b-col>    
              <b-col md="3">
                <FindObject render="search"
                            type="erpPointsSales" 
                            auto-select-single
                            display="full_identification"
                            @select-object="loadPointsSales($event)" 
                            :valueID="crud.form.points_sales_id"
                            :key="crud.form.points_sales_id"
                            :disabled="crud.form.id>0" 
                            :where="conditionPointsSalesActive" />                 
              </b-col>
            </b-row>
            <b-row v-if="balanceCurrentAccount>0">
              <b-col>
                <b-alert show variant="success">
                  Se encontró saldo a favor: <b>({{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(balanceCurrentAccount)}})</b>
                </b-alert>
              </b-col>
            </b-row>            
          </tab-content>
          
          <tab-content title="Imputación" icon="fa fa-paperclip" :before-change="paymentsValidImputation">                      
            <b-row>
              <b-col lg="12" md="12" sm="12">
                <b-table-simple hover small caption-top responsive v-if="arr.pending.length" class="app-table app-table--compact app-table--stacked-manual crud-payments-table-items">
                  <b-thead>
                    <b-tr>
                      <b-th width="10%" class="text-left">Imputar</b-th>
                      <b-th width="15%" class="text-left">Fecha</b-th>
                      <b-th width="40%" class="text-left">Comprobante</b-th>
                      <b-th width="10%" class="text-right">Total</b-th>
                      <b-th width="10%" class="text-right">Pendiente</b-th>
                      <b-th width="15%" class="text-center">A Imputar</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody v-for="(item, index) in arr.pending" :key="item.id">
                    <b-tr :key="itemForceUpdate" v-if="crud.formPending[index]">
                      <b-td data-label="Imputar" :class="'align-middle ' + alignClass('text-left')">
                        <b-form-checkbox v-model="crud.formPending[index].check"
                                          @change="validCheck(index)"
                                          switch
                                          size="sm"
                                          class="pull-left">
                        </b-form-checkbox>
                      </b-td>
                      <b-td data-label="Fecha" :class="'align-middle ' + alignClass('text-left')">
                        {{moment(item.date).format('DD/MM/YYYY')}}
                      </b-td>
                      <b-td data-label="Comprobante" :class="'align-middle ' + alignClass('text-left')">
                        {{item.type_voucher.name + ' ' + item.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')}}
                      </b-td>
                      <b-td data-label="Total" :class="'align-middle ' + alignClass('text-right')">
                        <div class="text-danger" v-if="item.type_voucher.type_balance == '-'">
                          <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(item.amount_total)}}</b>
                        </div>
                        <div class="text-success" v-else>
                          <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(item.amount_total)}}</b>
                        </div>
                      </b-td>
                      <b-td data-label="Pendiente" :class="'align-middle ' + alignClass('text-right')">
                        <div class="text-danger" v-if="item.type_voucher.type_balance == '-'">
                          <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(calcVoucherPending(item))}}</b>
                        </div>
                        <div class="text-success" v-else>
                          <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(calcVoucherPending(item))}}</b>
                        </div>
                      </b-td>
                      <b-td data-label="A Imputar" :class="'align-middle ' + alignClass('text-center')">
                        <app-number-input size="sm"
                                      @change="validAmount(index)"
                                      v-model="crud.formPending[index].amount"
                                      placeholder="Ingresar monto a imputar"
                                      class="crud-payments-item-input"
                                      v-if="crud.formPending[index].check" />
                      </b-td>
                    </b-tr>
                  </b-tbody>
                </b-table-simple>
                <EmptyState v-else icon="paperclip" title="No hay comprobantes pendientes de imputar" />
              </b-col>
            </b-row>
            <b-row align-h="end" class="mb-2">
              <b-col lg="4" md="4" sm="4">
                <b-input-group size="sm" prepend="TOTAL A IMPUTAR">
                  <b-form-input :value="imputationFormat"
                                type="text"
                                :readonly="true"
                                class="payments-payments-add-total-final">
                  </b-form-input>
                </b-input-group>
              </b-col>
            </b-row>
          </tab-content>

          <tab-content title="Detalle" icon="fa fa-list" :before-change="paymentsValidPayments">
            <b-row align-h="center"> 
              <b-col lg="4">             
                <FinanceSUB @getFinances="storeFinances($event)" 
                            v-if="crud.form.suppliers_id && crud.form.points_sales_id"
                            :points_sales="crud.form.points_sales"
                            :amount_impute="calc.imputation"
                            :reset="resetControl"/>              
              </b-col>
            </b-row>
          </tab-content>

          <tab-content title="Asiento Contable" icon="fa fa-balance-scale" :before-change="paymentsValidCrudAccounting" v-if="showContabilidad"> 
            <div v-if="contabilidad.render" :key="'accounting_' + itemAccountingForceUpdate">
              <AccountingEntries  :typeDebe="contabilidad.typeDebe"
                                  :typeHaber="contabilidad.typeHaber"
                                  :defaultDebe="contabilidad.defaultDebe"
                                  :defaultHaber="contabilidad.defaultHaber"
                                  :reference="contabilidad.reference"
                                  :amountTotal="contabilidad.amountTotal"
                                  @getAsiento="getAsiento($event)" />         
            </div>
          </tab-content>          

          <tab-content title="Observaciones" icon="fa fa-flag-checkered">          
            <b-row>
              <b-col md="12">
                <b-form-group label="Observaciones">
                  <app-textarea                
                    v-model="crud.form.observations"
                    placeholder="Ingresar las observaciones..."
                    rows="3"
                    max-rows="6"
                  ></app-textarea>
                </b-form-group>
              </b-col>         
            </b-row>
            <b-row v-if="crud.form.payment">
              <b-col>
                <b-alert show variant="warning" v-if="parseFloat(crud.form.payment.total) > parseFloat(calc.imputation)">
                  <AppIcon name="exclamation-triangle" class="mr-1" />El importe pagado es mayor al total de los comprobantes imputado.<br>
                  <b>Importe Imputado</b> {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(this.calc.imputation)}}<br>
                  <b>Importe Pagado</b> {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(this.crud.form.payment.total)}}<br><br>
                  <i>El saldo quedará en cuenta corriente pendiente de imputación a favor de la empresa</i>
                </b-alert>
              </b-col>
            </b-row>             
          </tab-content>

        </form-wizard>
        
        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="cancel()">Cancelar</b-button>              
        </div>                
      </b-modal>

      <!-- CRUD IMPUTACIONES -->
      <b-modal v-model="modal.imputation.active"
              no-close-on-esc
              no-close-on-backdrop
              hide-header-close
              size="xl"
              :key="modal.imputation._id"
              :id="modal.imputation._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="paperclip" /></span>
          <h5 class="app-modal-header__title">{{this.modal.imputation.title}}</h5>
        </div>
        
        <b-row align-h="end" class="mb-2">
          <b-col md="5">
            <FindObject render="search"
                        type="suppliers" 
                        @select-object="loadSuppliers($event)" 
                        :valueID="crud.form.suppliers_id"
                        :where="conditionSuppliersActive"
                        :disabled="crud.form.id>0" />                 
          </b-col>    
          <b-col md="3">
            <FindObject render="search"
                        type="erpPointsSales" 
                        auto-select-single
                        display="full_identification"
                        @select-object="loadPointsSales($event)" 
                        :valueID="crud.form.points_sales_id"
                        :key="crud.form.points_sales_id"
                        :disabled="crud.form.id>0" 
                        :where="conditionPointsSalesActive" />                 
          </b-col>          
          <b-col lg="4" md="4" sm="4" v-if="crud.form.payment">
            <b-input-group size="sm" prepend="TOTAL PAGADO" class="payments-imputation-total-align">
              <b-form-input v-model="crud.form.payment.totalFormat" 
                            type="text"                                
                            :readonly="true"
                            class="payments-payments-add-total">
              </b-form-input>   
            </b-input-group>                
          </b-col>
        </b-row>
        <b-row>
          <b-col lg="12" md="12" sm="12">
            <b-table-simple hover small caption-top responsive v-if="arr.pending.length" class="app-table app-table--compact app-table--stacked-manual crud-payments-table-items">
              <b-thead>
                <b-tr>
                  <b-th width="10%" class="text-left">Imputar</b-th>
                  <b-th width="15%" class="text-left">Fecha</b-th>
                  <b-th width="40%" class="text-left">Comprobante</b-th>
                  <b-th width="10%" class="text-right">Total</b-th>
                  <b-th width="10%" class="text-right">Pendiente</b-th>
                  <b-th width="15%" class="text-center">A Imputar</b-th>
                </b-tr>
              </b-thead>
              <b-tbody v-for="(item, index) in arr.pending" :key="item.id">
                <b-tr :key="itemForceUpdate" v-if="crud.formPending[index]">
                  <b-td data-label="Imputar" :class="'align-middle ' + alignClass('text-left')">
                    <b-form-checkbox v-model="crud.formPending[index].check"
                                      @change="validCheck(index)"
                                      switch
                                      size="sm"
                                      class="pull-left">
                    </b-form-checkbox>
                  </b-td>
                  <b-td data-label="Fecha" :class="'align-middle ' + alignClass('text-left')">
                    {{moment(item.date).format('DD/MM/YYYY')}}
                  </b-td>
                  <b-td data-label="Comprobante" :class="'align-middle ' + alignClass('text-left')">
                    {{item.type_voucher.name + ' ' + item.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')}}
                  </b-td>
                  <b-td data-label="Total" :class="'align-middle ' + alignClass('text-right')">
                    <div class="text-danger" v-if="item.type_voucher.type_balance == '-'">
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(item.amount_total)}}</b>
                    </div>
                    <div class="text-success" v-else>
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(item.amount_total)}}</b>
                    </div>
                  </b-td>
                  <b-td data-label="Pendiente" :class="'align-middle ' + alignClass('text-right')">
                    <div class="text-danger" v-if="item.type_voucher.type_balance == '-'">
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(calcVoucherPending(item))}}</b>
                    </div>
                    <div class="text-success" v-else>
                      <b>{{Intl.NumberFormat('es-AR',{style:'currency',currency: item.currency.code}).format(calcVoucherPending(item))}}</b>
                    </div>
                  </b-td>
                  <b-td data-label="A Imputar" :class="'align-middle ' + alignClass('text-center')">
                    <app-number-input size="sm"
                                  @change="validAmount(index)"
                                  v-model="crud.formPending[index].amount"
                                  placeholder="Ingresar monto a imputar"
                                  class="crud-payments-item-input"
                                  v-if="crud.formPending[index].check" />
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
            <EmptyState v-else icon="paperclip" title="No hay comprobantes pendientes de imputar" />
          </b-col>
        </b-row>
        <b-row align-h="end" class="mb-2">
          <b-col lg="8" md="8" sm="8" v-if="calc.positiveBalance<0">
            <b-alert show variant="danger">
              <AppIcon name="exclamation-triangle" class="mr-1" />Revise los comprobantes seleccionado, no puede imputar un monto mayor al importe total ingresado
            </b-alert>
          </b-col>
          <b-col lg="4" md="4" sm="4">
            <b-input-group size="sm" prepend="SALDO A FAVOR">
              <b-form-input v-model="calc.positiveBalanceFormat" 
                            type="text"
                            :readonly="true"
                            class="payments-payments-add-total-final">
              </b-form-input>   
            </b-input-group>                
          </b-col>
        </b-row>                        
          
        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="cancelImputation()">Cancelar</b-button>
          <b-button variant="dark" @click="saveImputation()"><AppIcon name="paperclip" class="mr-1" />Imputar</b-button>
        </div>
      </b-modal>

      <!-- CRUD DETAILS -->
      <b-modal v-model="modal.detail.active"
              size="xl"
              :key="modal.detail._id"
              :id="modal.detail._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="visibility" /></span>
          <h5 class="app-modal-header__title">{{this.modal.detail.title}}</h5>
        </div>

        <b-row>
          <b-col sm="12">
            <b-tabs active-nav-item-class="font-weight-bold text-uppercase text-dark">
              <b-tab>
                <template #title><AppIcon name="description" class="mr-1" /> General</template>
                <div class="app-modal-detail-list" v-if="itemSelected">
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">ID</span>
                    <span class="app-modal-detail-list__value"><b>#{{this.itemSelected.id}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Empresa</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.point_sale.business.reference}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.type_voucher && itemSelected.point_sale">
                    <span class="app-modal-detail-list__label">Comprobante</span>
                    <span class="app-modal-detail-list__value"><b>{{this.itemSelected.type_voucher.name + ' '  + this.itemSelected.point_sale.point_sale.toString().padStart(4,'0') + '-' + this.itemSelected.number.toString().padStart(8,'0')}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.staff">
                    <span class="app-modal-detail-list__label">Emisor</span>
                    <span class="app-modal-detail-list__value">{{itemSelected.staff.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row">
                    <span class="app-modal-detail-list__label">Fecha</span>
                    <span class="app-modal-detail-list__value">{{moment(this.itemSelected.date).format('DD MMMM YYYY')}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.point_sale">
                    <span class="app-modal-detail-list__label">Punto de Venta</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.point_sale.point_sale}} - {{this.itemSelected.point_sale.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.supplier">
                    <span class="app-modal-detail-list__label">Proveedor</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.supplier.name}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.amount_total">
                    <span class="app-modal-detail-list__label">Total</span>
                    <span class="app-modal-detail-list__value"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency:this.getCurrencyCode}).format(this.itemSelected.amount_total)}}</b></span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.observations">
                    <span class="app-modal-detail-list__label">Observaciones</span>
                    <span class="app-modal-detail-list__value">{{this.itemSelected.observations}}</span>
                  </div>
                  <div class="app-modal-detail-list__row" v-if="itemSelected.amount_pending">
                    <span class="app-modal-detail-list__label">Pendiente de Imputación</span>
                    <span class="app-modal-detail-list__value app-modal-detail-list__value--warning"><b>{{Intl.NumberFormat('es-AR',{style:'currency',currency:this.getCurrencyCode}).format(this.itemSelected.amount_pending)}}</b></span>
                  </div>
                </div>
              </b-tab>
              <b-tab>
                <template #title><AppIcon name="money" class="mr-1" /> Detalle de Pago</template>
                <b-table class="mb-0 table-full-detail-payments app-table app-table--compact"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetailPayment.items"
                        :fields="tableDetailPayment.fields"
                        v-if="tableDetailPayment.items.length">
                    
                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"                    
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>           

                  <template v-slot:cell(medio)="row">
                    <div v-if="row.item.type=='CASH'" class="text-truncate" v-b-tooltip.hover :title="row.item.relation.cash.name">
                      {{row.item.relation.cash.name}}
                    </div>
                    <div v-if="row.item.type=='CHEQUE'">
                      CHEQUE
                    </div>
                  </template>

                  <template v-slot:cell(description)="row">
                    <div v-if="row.item.type=='CASH'" class="text-truncate" v-b-tooltip.hover :title="'CAJA/CUENTA: ' + row.item.relation.cash.name + ' | MONEDA: ' + row.item.relation.currency.name">
                      <b>CAJA/CUENTA:</b> {{row.item.relation.cash.name}} |
                      <b>MONEDA:</b> {{row.item.relation.currency.name}}
                    </div>
                    <div v-if="row.item.type=='CHEQUE'">
                      <b>ID:</b> {{row.item.relation.id}} |
                      <b>NUMERO:</b> {{row.item.relation.number}} |
                      <b>BANCO:</b> {{row.item.relation.bank}} |
                      <b>EMISION:</b> {{moment(row.item.relation.date).format('DD/MM/YYYY')}} |
                      <b>VENCIMIENTO:</b> {{moment(row.item.relation.date_expiration).format('DD/MM/YYYY')}} |
                      <b-link :id="'more-data-cheque-' + row.index">Más Datos ...</b-link>

                      <b-popover :target="'more-data-cheque-' + row.index" triggers="hover" placements="auto">
                        <template #title>Datos Cheque</template>
                        <b>ID:</b> {{row.item.relation.id}} <br>
                        <b>NUMERO:</b> {{row.item.relation.number}} <br>
                        <b>BANCO:</b> {{row.item.relation.bank}} <br>
                        <b>EMISION:</b> {{moment(row.item.relation.date).format('DD/MM/YYYY')}} <br>
                        <b>VENCIMIENTO:</b> {{moment(row.item.relation.date_expiration).format('DD/MM/YYYY')}} <br>
                        <b>EMISOR:</b> {{row.item.relation.transmitter_name}} <br>
                        <b>CUIT:</b> {{row.item.relation.transmitter_documents_number}} <br>
                        <b>OBSERVACIONES:</b> {{row.item.relation.observations}}
                      </b-popover>                      
                    </div>
                  </template>                  

                  <template v-slot:cell(amount)="row">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(row.item.amount_total)}}
                  </template>                  
                  
                </b-table>
                <EmptyState v-else icon="reports" title="No se encontraron registros" />
              </b-tab>
              <b-tab>
                <template #title><AppIcon name="paperclip" class="mr-1" /> Comprobantes Imputados</template>
                <b-table class="mb-0 table-full-detail-payments app-table app-table--compact"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableDetailImputation.items"
                        :fields="tableDetailImputation.fields"
                        v-if="tableDetailImputation.items.length">
                    
                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"                    
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>           

                  <template v-slot:cell(date)="row"> 
                    {{moment(row.item.purchase.date).format('DD/MM/YYYY')}}
                  </template>

                  <template v-slot:cell(voucher)="row">
                    <div class="text-truncate" v-b-tooltip.hover :title="row.item.purchase.type_voucher.name + ' ' + row.item.purchase.point_sale.toString().padStart(4,'0') + '-' + row.item.purchase.number.toString().padStart(8,'0')">
                      {{row.item.purchase.type_voucher.name + ' ' + row.item.purchase.point_sale.toString().padStart(4,'0') + '-' + row.item.purchase.number.toString().padStart(8,'0')}}
                    </div>
                  </template>

                  <template v-slot:cell(amount_total)="row">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(row.item.purchase.amount_total)}}
                  </template>               

                  <template v-slot:cell(amount_pending)="row">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(calcPendingVoucher(row.item))}}
                  </template>                                 
                  
                  <template v-slot:cell(amount_impute)="row">
                    {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(row.item.amount_total)}}
                  </template>                                 
                </b-table>
                <EmptyState v-else icon="reports" title="No se encontraron registros" />
              </b-tab>
              <b-tab v-if="showContabilidad">
                <template #title><AppIcon name="bar-chart" class="mr-1" /> Asiento</template>
                <b-table class="mb-0 table-full-detail-sales app-table app-table--compact"
                        responsive
                        :stacked="isMobileViewport ? 'md' : false"
                        :hover="true"
                        :small="true"
                        :fixed="true"
                        :items="tableAccounting.items"
                        :fields="tableAccounting.fields"
                        v-if="tableAccounting.items.length">
                    
                  <template v-slot:table-colgroup="scope">
                    <col
                      v-for="field in scope.fields"                    
                      :key="field.key"
                      :style="{ width: field.width }"
                    >
                  </template>                                                                                     

                  <template v-slot:cell(code)="data">
                    <div v-if="data.item.type=='Debe'">
                      <span>{{data.item.accounting_accounts_code}}</span>
                    </div>
                    <div v-if="data.item.type=='Haber'">
                      <span class="ml-5">{{data.item.accounting_accounts_code}}</span>
                    </div>
                  </template>  

                  <template v-slot:cell(name)="data">
                    <div v-if="data.item.type=='Debe'">
                      <span>{{data.item.accounting_accounts_name}}</span>
                    </div>
                    <div v-if="data.item.type=='Haber'">
                      <span class="ml-5">{{data.item.accounting_accounts_name}}</span>
                    </div>                                                          
                  </template>

                  <template v-slot:cell(debit)="data">
                    <span v-if="data.item.type == 'Debe'">
                      {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(data.item.total)}}
                    </span>
                  </template>

                  <template v-slot:cell(credit)="data">
                    <span v-if="data.item.type == 'Haber'">
                      {{Intl.NumberFormat('es-AR',{style:'currency',currency: getCurrencyCode}).format(data.item.total)}}
                    </span>
                  </template>
                </b-table>
                <EmptyState v-else icon="reports" title="No se encontraron registros" />
              </b-tab>
            </b-tabs>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.detail.active=false"><AppIcon name="x" class="mr-1" /> Cerrar</b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import ErrorToken from '@/handler/errorToken'
  import Session from '@/handler/session'  
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import Param from '@/config/parameters'
  import FindObject from '@/components/inc/find/findObject'    
  import Printer from '@/components/inc/printer/printer'  
  import AccountingEntries from '@/components/inc/accounting/entries'  
  import moment from 'moment'
  import {FormWizard, TabContent} from 'vue-form-wizard'
  import 'vue-form-wizard/dist/vue-form-wizard.min.css'
  import FinanceSUB from '@/components/inc/finances/sub'
  import Funciones from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      FindObject,
      Printer,
      FormWizard,
      TabContent,
      FinanceSUB,
      AccountingEntries,
      TabHeaderActions,
    },
    data: () => {
      return {      
        access: {
          module_id: Modules.PAGOS_ERP,
          profile_id: Profiles.PERSONAL,
          view_reference: 'payments',
          elements: {
            deletePayments: true             
          }
        },
        table : {
          items: [],
          fields: [],
          currentPage: 1,
          perPage: 0,
          isBusy: false,
          tablePaginate: null,
        },
        tableSubPayment : {
          items: [],
          fields: []
        },            
        tableSubImputation : {
          items: [],
          fields: []
        },                    
        tableDetailPayment : {
          items: [],
          fields: []
        },       
        tableAccounting : {
          items: [],
          fields: []
        },          
        tableDetailImputation : {
          items: [],
          fields: []
        },                    
        crud: {
          form: {
            id: 0,
            date: '',            
            types_vouchers_id: 0,
            types_vouchers: null,
            letter: '',
            number: 0,
            points_sales_id: 0,
            points_sales: null,              
            observations: '',            
            staff_id: 0,
            suppliers_id: 0,
            suppliers: null,  
            accountingEntry: null,  
            selectTypeVoucher: 'ordenpago',
            reference_voucher_id: 0,                                                           
            payment: null,
            imputation: [],    
            operation: 'add',
          },                        
          formPending: [],       
          print: {
            reference: '',
            id: 0,
            key: 0,
          }               
        },
        modal: {
          form: {
            active: false,
            title: '',
          },
          imputation: {
            active: false,
            title: '',
          },
          detail: {
            active: false, 
            title: '',
          },            
        },          
        arr: {
          payments: [],  
          pending: [],
          export: [],           
          filters : {
            suppliers: [],            
            points_sales: [],            
          },                              
        },           
        filters: {
          date_start: '',
          date_end: '',
          suppliers: null,
          points_sales: null,
        },     
        itemSelected: null,
        detailDrawer: false,
        // Panel de filtros: cerrado por default; el usuario que lo abre
        // queda con su preferencia persistida (ver watcher filtersOpen).
        filtersOpen: false,
        // stacked (modo card mobile) y sticky-header son mutuamente
        // excluyentes en BootstrapVue (hasStickyHeader = !isStacked && ...,
        // ver td.js/tbody.js) — pasar "md" fijo mataba el sticky-header
        // también en desktop. Por eso stacked se activa solo cuando el
        // viewport realmente está en mobile (ver mounted/mobileMql).
        isMobileViewport: false,
        itemForceUpdate: 0,
        resetControl: false,
        calc: {
          imputation: 0,
          positiveBalance: 0,
        },        
        itemAccountingForceUpdate: 0,    
        contabilidad: {
          render: false,
          typeDebe:"",                                 
          typeHaber:"",
          defaultDebe:[],
          defaultHaber:[],
          reference:'',
          amountTotal:0
        },      
        balanceCurrentAccount: 0,     
      }
    },
    created () {     
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      this.access.elements.deletePayments = Helper.hasAccessElement(this.access, 'delete_payments')
      /* Fin configuracion */
    },     
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_payments') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldTable()
      this.filterLoad()
      this.filterPayments()

      // Acción directa desde la paleta de comandos: abrir el alta al entrar.
      if (this.$route.query.new == '1') {
        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }

      this._start = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.filterLoad()
      this.filterPayments()

      // Misma acción directa que en mounted(): si la pestaña ya estaba
      // montada (keep-alive), "mounted" no vuelve a correr y la paleta de
      // comandos no abriría nada sin este chequeo acá también.
      if (this.$route.query.new == '1') {
        this.add()
        // Limpia el query apenas se consume -- si no, la pestaña queda con
        // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
        // reabriría el alta de nuevo aunque el usuario solo quiera ver el
        // listado.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
      }
    },
    computed: {
      // NAVEGACION DEL DRAWER (anterior/siguiente sobre las filas cargadas)
      currentPaymentIndex() {
        if(!this.itemSelected || !this.table.items) {
          return -1
        }
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrevPayment() {
        return this.currentPaymentIndex > 0
      },
      canGoNextPayment() {
        if(this.currentPaymentIndex < 0) {
          return false
        }
        return this.currentPaymentIndex < (this.table.items.length - 1)
      },
      // El input de solo lectura "TOTAL A IMPUTAR" mostraba calc.imputation
      // crudo (sin separador de miles/decimales).
      imputationFormat() {
        return Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.calc.imputation || 0)
      },

      // CONDITIONS SELECT
      conditionSuppliersActive(){
        return [{field: 'active', condition: true}];
      },  
      conditionPointsSalesActive() {
        return [{field: 'active', condition: true}];
      },  
      
      // CURRENCY
      getCurrencySymbol() {
        return '$'
      },  
      getCurrencyCode() {
        return 'ARS'
      },  

      // IMPUTATION
      getImputation() {
        var imputation = 0
        if(this.crud.form.payment) {
          if(this.crud.formPending) {                        
            this.crud.formPending.forEach(element => {                             
              if(element.check) {    
                if(element.operation == '+') {
                  imputation = parseFloat(imputation) + parseFloat(element.amount)
                } else {
                  imputation = parseFloat(imputation) - parseFloat(element.amount)
                }
              }
            });   
          }
        }
        return imputation        
      },

      // CONFIGURACION
      showContabilidad() {
        var status = false
        var modules = Session.getSession().auth.user.permissions.modules        
        modules.forEach(element => {
          if(element.id == Modules.CONTABILIDAD) {                             
            status = true                       
          }
        })        
        return status
      },      

      // VOUCHER
      getRefVoucher() {        
        if(this.crud.form.types_vouchers && this.crud.form.points_sales && this.crud.form.number) {
          return this.crud.form.types_vouchers.name + ' '  + this.crud.form.points_sales.point_sale.toString().padStart(4,'0') + '-' + this.crud.form.number.toString().padStart(8,'0')  
        } else {
          return ''
        }      
      },       
    },    
    watch: {
      filtersOpen: function (value) {
        StorageLocal.setValue('filters_open_crud_payments', value ? 'true' : 'false')
      },
      // Si el viewport cruza el breakpoint mobile (resize/rotación), las
      // clases de alineación de table.fields tienen que recalcularse.
      isMobileViewport() {
        this.loadFieldTable()
      },
      'crud.form.suppliers_id': function (newQuestion, oldQuestion) {
        if(this.crud.form.operation == 'add') {
          this.crud.form.payment = null           
          this.crud.form.imputation = []          
          this.resetControl = true

          this.arr.pending = []
          this.crud.formPending = []
          this.calc.imputation = 0
          this.balanceCurrentAccount = 0  
                    
          this.obtenerVouchersPendingImputarBySuppliers()
          this.searchVoucherBySupplier()
        }
      },
      'crud.form.points_sales_id': function (newQuestion, oldQuestion) {                
        if(this.crud.form.operation == 'add') {
          this.crud.form.payment = null           
          this.crud.form.imputation = []          
          this.resetControl = true

          this.arr.pending = []
          this.crud.formPending = []
          this.calc.imputation = 0
          this.balanceCurrentAccount = 0  
                    
          this.obtenerVouchersPendingImputarBySuppliers()        
          this.searchVoucherBySupplier()
        }
      },
    },    
    methods: {
      // CONFIGURACION
      // RESPONSIVE
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      // Alineación de columna: en mobile (modo stacked) el valor pasa a
      // apilarse debajo de su label, así que la alineación de columna de
      // desktop (centrado/derecha) no tiene sentido — se resuelve acá, en el
      // propio JSON de fields, en vez de pelear contra la clase con CSS.
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      loadFieldTable () {
        // TABLE COMPROBANTES
        this.table.fields = []
        this.table.fields.push({key: 'business', label: 'Empresa', class:"align-middle " + this.alignClass('text-left'), width:"60px"})
        this.table.fields.push({key: 'id', label: 'Nº', class:"align-middle " + this.alignClass('text-center'), width:"120px"})
        this.table.fields.push({key: 'date', label: 'Fecha', class:"align-middle " + this.alignClass('text-center'), width:"80px"})
        this.table.fields.push({key: 'suppliers_id', label: 'Proveedores', class:"align-middle", width:"200px"})
        this.table.fields.push({key: 'staff_id', label: 'Staff', class:"align-middle " + this.alignClass('text-center'), width:"60px"})
        this.table.fields.push({key: 'amount_total', label: 'Total', class:"align-middle " + this.alignClass('text-right'), width:"60px"})
        this.table.fields.push({key: 'amount_pending', label: 'Imputar', class:"align-middle " + this.alignClass('text-right'), width:"60px"})
        // En mobile va como texto plano (no ícono+tooltip), así que necesita
        // un label real; en desktop es una columna angosta solo-ícono.
        this.table.fields.push({key: 'observations', label: this.isMobileViewport ? 'Observaciones' : '', class:"align-middle " + this.alignClass('text-center'), width:"25px"})
        // El kebab de acciones no va en mobile: el drawer ya tiene las
        // mismas acciones desplegadas (Imprimir/Imputar/Eliminar), visibles
        // ahí en cualquier ancho.
        if(!this.isMobileViewport) {
          this.table.fields.push({key: 'f_action', label:'', class:"align-middle", width:"60px"})
        }

        // TABLE DETALLE
        this.tableSubPayment.fields = []
        this.tableSubPayment.fields.push({key: 'medio', label: 'Medio', class:"align-middle " + this.alignClass('text-left') + " text-truncate", width:"45%"})
        this.tableSubPayment.fields.push({key: 'amount', label: 'Importe', class:"align-middle " + this.alignClass('text-right'), width:"30%"})
        this.tableSubPayment.fields.push({key: 'f_action', label:'', class:"align-middle " + this.alignClass('text-right'), width:"25%"})

        // TABLE IMPUTATION
        this.tableSubImputation.fields = []
        this.tableSubImputation.fields.push({key: 'voucher', label: 'Comprobante', class:"align-middle " + this.alignClass('text-left') + " text-truncate", width:"45%"})
        this.tableSubImputation.fields.push({key: 'amount', label: 'Importe', class:"align-middle " + this.alignClass('text-right'), width:"30%"})
        // En mobile el botón queda "perdido" sin nada que lo identifique
        // (columna sin label + ícono solo) — en ese ancho lleva título de
        // columna y texto visible; en desktop sigue siendo ícono+tooltip.
        this.tableSubImputation.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acciones' : '', class:"align-middle " + this.alignClass('text-right'), width:"25%"})

        // TABLE DETALLE COMPLETO
        this.tableDetailPayment.fields = []
        this.tableDetailPayment.fields.push({key: 'medio', label: 'Medio', class:"align-middle " + this.alignClass('text-left') + " text-truncate", width:"20%"})
        this.tableDetailPayment.fields.push({key: 'description', label: 'Detalle', class:"align-middle " + this.alignClass('text-left') + " text-truncate", width:"60%"})
        this.tableDetailPayment.fields.push({key: 'amount', label: 'Importe', class:"align-middle " + this.alignClass('text-right'), width:"20%"})

        // TABLE ASIENTO
        this.tableAccounting.fields = []
        this.tableAccounting.fields.push({key: 'code', label: 'Código', class:"align-middle", width:"150px"})
        this.tableAccounting.fields.push({key: 'name', label: 'Nombre', class:"align-middle", width:"250px"})
        this.tableAccounting.fields.push({key: 'debit', label: 'Debe', class:"align-middle", width:"150px"})
        this.tableAccounting.fields.push({key: 'credit', label: 'Haber', class:"align-middle", width:"150px"})

        // TABLE DETALLE DE IMPUTATION
        this.tableDetailImputation.fields = []
        this.tableDetailImputation.fields.push({key: 'date', label: 'Fecha', class:"align-middle " + this.alignClass('text-center'), width:"15%"})
        this.tableDetailImputation.fields.push({key: 'voucher', label: 'Comprobante', class:"align-middle " + this.alignClass('text-left') + " text-truncate", width:"40%"})
        this.tableDetailImputation.fields.push({key: 'amount_total', label: 'Importe Total', class:"align-middle " + this.alignClass('text-right'), width:"15%"})
        this.tableDetailImputation.fields.push({key: 'amount_pending', label: 'Importe Pendiente', class:"align-middle " + this.alignClass('text-right'), width:"15%"})
        this.tableDetailImputation.fields.push({key: 'amount_impute', label: 'Importe Imputado', class:"align-middle " + this.alignClass('text-right'), width:"15%"})
      },
      getRowCount(items) {
        return items.length
      },
      // Avatar con iniciales cuando no hay imagen (mismo helper que el
      // avatar de sesión en DefaultHeaderDropdownAccnt.vue: inicial + color
      // determinístico por nombre).
      avatarFromName(name) {
        return Funciones.avatarFromName(name)
      },
      // Confirm estándar: header con ícono + tinte suave (.app-confirm-header)
      // en vez de header-bg-variant sólido, size 'sm' siempre (es solo texto
      // corto), y okVariant/headerClass según sea o no destructiva.
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      // Click en la fila abre el drawer de detalle, salvo que el click haya
      // sido sobre un control (el dropdown de acciones, un link, etc.).
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      // Resalta en la tabla la fila del registro que está abierto en el
      // drawer, para no perder la referencia visual mientras se navega con
      // los botones anterior/siguiente.
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        return ''
      },
      setConfigTableSubShow() {
        var arrTable = document.getElementsByClassName('table-payments-sub')
        var i = 0
        for(i=0; i<arrTable.length; i++) {
          arrTable[i].style.minHeight = '210px'
        }        
      },
      setConfigTableSubHide() {
        var arrTable = document.getElementsByClassName('table-payments-sub')
        var i = 0
        for(i=0; i<arrTable.length; i++) {
          arrTable[i].style.minHeight = 'auto'
        }        
      },  

      // SELECT
      loadSuppliers(object) {        
        if(object){
          this.crud.form.suppliers = object
          this.crud.form.suppliers_id = object.id   
          
          if(object.data_fiscal) {
            if(object.data_fiscal.points_sales_id) {
              if(!this.crud.form.points_sales_id) {
                this.crud.form.points_sales = object.data_fiscal.points_sales
                this.crud.form.points_sales_id = object.data_fiscal.points_sales_id
              }
            }
          }

          this.getBalanceCurrentAccount()
        } else {
          this.crud.form.suppliers = null
          this.crud.form.suppliers_id = 0

          this.balanceCurrentAccount = 0
        }
      }, 
      loadPointsSales(object) {                
        if(object){
          this.crud.form.points_sales = object
          this.crud.form.points_sales_id = object.id           
          
          this.getBalanceCurrentAccount()
        } else {
          this.crud.form.points_sales = null
          this.crud.form.points_sales_id = 0

          this.getBalanceCurrentAccount()
        }
      },
      prepareExport(data) {        
        this.arr.export = []
        data.forEach(element => {
            var businessID = 0
            var businessName = ""          
            var suppliersID = 0
            var suppliersCode = ""
            var suppliersName = ""
            var voucherReference = ""

            if(element.point_sale && element.point_sale.business) {
              businessID = element.point_sale.business.id            
              businessName = element.point_sale.business.reference
            }
            if(element.supplier) {
              suppliersID = element.supplier.id
              suppliersCode = element.supplier.code
              suppliersName = element.supplier.name            
            }
            if(element.type_voucher) {            
              voucherReference = element.type_voucher.name + ' ' + element.point_sale.toString().padStart(4,'0') + '-' + element.number.toString().padStart(8,'0')
            }         

            this.arr.export.push({
              id: element.id,
              businessID: businessID,
              businessName: businessName,              
              date: element.date,      
              voucherReference: voucherReference,        
              suppliersID: suppliersID,
              suppliersCode: suppliersCode,
              suppliersName: suppliersName,                                 
              total: element.amount_total.replace('.',','),            
              total_pending: element.amount_pending,            
              observations: element.observations,    
              payment_medio: '',
              payment_amount: 0,
              imputation_reference: '',
              imputation_amount: 0,
            })

            element.details_payments.forEach(element1 => {            
              this.arr.export.push({
                payment_medio: element1.type,
                payment_amount: element1.amount_total.replace('.',','),
              })
            })

            element.details_imputations.forEach(element1 => {   
              var voucherReferenceImputation = ''
              if(element1.purchase.type_voucher) {            
                voucherReferenceImputation = element1.purchase.type_voucher.name + ' ' + element1.purchase.point_sale.toString().padStart(4,'0') + '-' + element1.purchase.number.toString().padStart(8,'0')
              }         

              this.arr.export.push({
                imputation_reference: voucherReferenceImputation,
                imputation_amount: element1.amount_total.replace('.',','),
              })
            })            
          });
      },

     // AMOUNT CURRENT ACCOUNT
      getBalanceCurrentAccount() {
        var result = serviceAPI.filtrarCurrentsAccountsBalance({
          suppliers_id: this.crud.form.suppliers_id,
          points_sales_id: this.crud.form.points_sales_id, 
        })

        result.then((response) => {
          var data = response.data
          this.balanceCurrentAccount = data
        })
      },

      // ABM MAIN
      add() {
        this.crud.form.operation = 'add'

        this.crud.form.id = 0           
        this.crud.form.date = moment().format('YYYY-MM-DD')        
        this.crud.form.observations = ''
        this.crud.form.staff_id = Helper.getStaffId()
        this.crud.form.suppliers_id = 0
        this.crud.form.suppliers = null   
        this.crud.form.points_sales_id = 0
        this.crud.form.points_sales = null        
        this.crud.form.types_vouchers_id = 0
        this.crud.form.types_vouchers = null
        this.crud.form.letter = ''
        this.crud.form.number = 0
        this.crud.form.payment = null
        this.crud.form.imputation = []         

        this.crud.formPending = []
        this.arr.pending = []

        this.calc.imputation = 0 
        this.balanceCurrentAccount = 0       

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Pago"
        this.modal.form.active = true
      },            
      remove(item) {
        this.crud.form.id = item.id
        
        var voucher = "ID #" + item.id
        if(item.type_voucher && item.point_sale) {
          voucher = item.type_voucher.name + ' ' + item.point_sale.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')
        }        

        this.confirmDialog('¿Desea borrar el Pago ' + voucher + '?', {
          title: 'Borrar Pago',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();
            var result = serviceAPI.eliminarPagos(this.crud.form.id);

            result.then((response) => {
              this.modal.form.active = false
              loader.hide()
              // Si el pago eliminado es el que está abierto en el drawer,
              // cerrarlo — si no, queda mostrando un registro ya borrado con
              // sus botones (Imprimir/Imputar/Eliminar) todavía activos.
              if(this.itemSelected && this.itemSelected.id === item.id) {
                this.hideSub()
              }
              this.filterPayments()
              this.$awn.success("Registro eliminado");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })
      },      
      save() {
        this.confirmDialog('¿Desea continuar con la generación del Pago?', {
          title: 'Generar Pago',
          okTitle: 'GENERAR'
        })
        .then(value => {
          if (value) {          
            let loader = this.$loading.show();
              
            var result = serviceAPI.agregarPagos(this.crud.form);

            result.then((response) => {
              this.modal.form.active = false
              loader.hide()
              this.filterPayments()
              this.$awn.success("Pago generado con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })              
      },          
      cancel() {
        this.confirmDialog('¿Desea cancelar el Pago?', {
          title: 'Cancelar el Pago',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {        
            this.modal.form.active = false
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })   
      },

      // ABM IMPUTATION
      addImputation(item) {
        this.crud.form.operation = 'imputation'

        this.crud.form.id = item.id           
        this.crud.form.suppliers_id = item.suppliers_id
        this.crud.form.suppliers = item.suppliers
        this.crud.form.points_sales_id = item.points_sales_id
        this.crud.form.points_sales = item.points_sales                                                 

        this.crud.formPending = []
        this.arr.pending = []
        this.obtenerVouchersPendingImputarBySuppliers()

        this.crud.form.payment = {}
        this.crud.form.payment.total = item.amount_pending
        this.crud.form.payment.totalFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.crud.form.payment.total)

        this.calc.imputation = 0
        this.calc.positiveBalance = item.amount_pending         
        this.calc.positiveBalanceFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.calc.positiveBalance)             

        this.modal.imputation._id = Math.random().toString(36).slice(2, 8)
        var reference = item.type_voucher.name + ' ' + item.point_sale.point_sale.toString().padStart(4,'0') + '-' + item.number.toString().padStart(8,'0')
        this.modal.imputation.title = "Imputación de comprobantes (" + reference + ")"
        this.modal.imputation.active = true
      },
      saveImputation() {
        this.confirmDialog('¿Desea continuar con la imputación de comprobantes?', {
          title: 'Generar Imputación',
          icon: 'paperclip',
          okTitle: 'GENERAR'
        })
        .then(value => {
          if (value) {
            let loader = this.$loading.show();

            this.loadFormImpuation()
            var result = serviceAPI.imputarPagos(this.crud.form);

            result.then((response) => {
              this.modal.imputation.active = false
              loader.hide()
              this.filterPayments(true)
              this.$awn.success("Imputación generada con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })              
      },          
      cancelImputation() {
        this.confirmDialog('¿Desea cancelar la imputación de Comprobantes?', {
          title: 'Cancelar Imputación',
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (value) {        
            this.modal.imputation.active = false
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })   
      },      
      saveDesimputar(item) {
        var reference = item.purchase.type_voucher.name + ' ' + item.purchase.point_sale.toString().padStart(4,'0') + '-' + item.purchase.number.toString().padStart(8,'0')
        this.confirmDialog('¿Desea continuar con la desimputación del comprobante ' + reference + '?', {
          title: 'Desimputar Comprobante',
          icon: 'link_off',
          danger: true,
          okTitle: 'DESIMPUTAR'
        })
        .then(value => {
          if (value) {          
            let loader = this.$loading.show();
            
            this.loadFormImpuation()
            var result = serviceAPI.desimputarPagos({
              details_imputation_id: item.id
            });

            result.then((response) => {              
              loader.hide()
              this.filterPayments(true)              
              this.$awn.success("Desimputación realizada con éxito");
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })      
      },

      // FINANCES
      storeFinances(values) {
        this.crud.form.payment = values           
        this.crud.form.payment.totalFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.crud.form.payment.total)
        
        this.calc.positiveBalance = this.crud.form.payment.total 
        this.calc.positiveBalanceFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.calc.positiveBalance)             

        this.resetControl = false
      },

      // VALIDATION STEP
      paymentsValidGeneral() {
        if(this.crud.form.suppliers_id && this.crud.form.points_sales_id) {                            
          return true;
        } else {
          if(!this.crud.form.suppliers_id) {
            this.$awn.alert("No se cargó el proveedor");
            return false;
          }
          if(!this.crud.form.points_sales_id) {
            this.$awn.alert("No se cargó el punto de venta");
            return false;
          }
        }        
      },    
      paymentsValidImputation() {    
        this.loadFormImpuation()
        return true;        
      },       
      paymentsValidPayments() {
        if(this.crud.form.payment) {
          if(this.crud.form.payment.total>0) {
            if(this.crud.form.payment.total >= this.calc.imputation ) {
              this.getContabilidad()
              return true
            } else {
              this.$awn.alert("El total del pago no puede ser menor al total imputado");
              return false;                        
            }
          } else {
            this.$awn.alert("No se cargó ningún método de pago");
            return false;          
          }
        } else {
          this.$awn.alert("No se cargó ningún método de pago");
          return false;                    
        }
      },   
      paymentsValidCrudAccounting() {        
        if(this.crud.form.accountingEntry) {
          return true
        } else {
          this.$awn.alert("Revisar el Asiento Contable");
          return false
        }
      },

      // IMPUTACION COMPROBANTES
      obtenerVouchersPendingImputarBySuppliers() {
        this.crud.formPending = []
        
        if(this.crud.form.suppliers_id && this.crud.form.points_sales_id) {
          var result = serviceAPI.filtrarPendienteImputar({
            suppliers_id:this.crud.form.suppliers_id, 
            points_sales_id: this.crud.form.points_sales_id,
            hide_note_credit: true,
          })        
          result.then((response) => {
            var data = response.data    
            
            this.arr.pending = data   
            this.arr.pending.forEach(element => {                        
              this.crud.formPending.push({              
                check: false,
                id: element.id,                            
                amount: parseFloat(element.amount_total) - parseFloat(element.amount_impute),
                maxAmount: parseFloat(element.amount_total) - parseFloat(element.amount_impute),   
                operation: element.type_voucher.type_balance,
              })
            });
          })                   
        }
      },      
      calcVoucherPending(item) {
        return parseFloat(item.amount_total) - parseFloat(item.amount_impute)
      },
      validCheck(index) {             
        if(this.crud.formPending[index].check) {          
          this.crud.formPending[index].amount = parseFloat(this.crud.formPending[index].maxAmount).toFixed(2)        
          this.forceUpdateItem()
        }

        this.validImputation(index)
        this.getAmountImputation()
      },
      validAmount(index){       
        if(this.crud.formPending[index].amount=='') {
          this.crud.formPending[index].amount = 0
          this.crud.formPending[index].check = false
        }

        if( parseFloat(this.crud.formPending[index].amount) > parseFloat(this.crud.formPending[index].maxAmount)) {          
          this.crud.formPending[index].amount = parseFloat(this.crud.formPending[index].maxAmount).toFixed(2)
          this.forceUpdateItem()
        }

        if( parseFloat(this.crud.formPending[index].amount) <= 0) {          
          this.crud.formPending[index].amount = parseFloat(this.crud.formPending[index].maxAmount).toFixed(2)          
          this.crud.formPending[index].check = false
          this.forceUpdateItem()
        }        

        this.validImputation(index)
        this.getAmountImputation()   
      },
      validImputation(index) {
        if(this.crud.form.payment) {
          if(this.crud.formPending) {            
            var imputation = this.getImputation

            var amount = 0
            var valid = parseFloat(this.crud.form.payment.total) - parseFloat(imputation)                        
            if( valid.toFixed(2) < 0) {
              amount = parseFloat(this.crud.formPending[index].amount) - (parseFloat(imputation) - parseFloat(this.crud.form.payment.total))
              this.crud.formPending[index].amount = parseFloat(amount.toFixed(2))
                          
              if(Math.abs(parseFloat(amount.toFixed(2))) <= 0) {
                this.crud.formPending[index].check = false
              }

              var positiveBalance = parseFloat(this.crud.form.payment.total) - parseFloat(this.getImputation)            
              this.calc.positiveBalance = parseFloat(positiveBalance.toFixed(2))
              this.calc.positiveBalanceFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.calc.positiveBalance)             
              
              this.forceUpdateItem()
            } else {
              amount = parseFloat(this.crud.form.payment.total) - parseFloat(imputation)              
              this.calc.positiveBalance = parseFloat(amount.toFixed(2))
              this.calc.positiveBalanceFormat = Intl.NumberFormat('es-AR',{style:'currency',currency: this.getCurrencyCode}).format(this.calc.positiveBalance)             

              this.forceUpdateItem()
            }
          }
        }        
      },      
      getAmountImputation() {        
        var imputation = 0
        if(this.crud.formPending) {          
          this.crud.formPending.forEach(element => {             
            if(element.check) {    
              if(element.operation == '+') {
                imputation = parseFloat(imputation) + parseFloat(element.amount)
              } else {
                imputation = parseFloat(imputation) - parseFloat(element.amount)
              }
            }
          });   
        }        
        this.calc.imputation = parseFloat(imputation.toFixed(2))
        this.resetControl = true   
      },
      loadFormImpuation() {
        this.crud.form.imputation = []        
        if(this.crud.formPending.length) {
          this.crud.formPending.forEach(element => {                  
            if(element.check && element.amount>0) {              
              this.crud.form.imputation.push({                
                'id': element.id,               
                'amount': element.amount,               
              })                            
            }
          });
        }        
      },
      forceUpdateItem() {
        this.itemForceUpdate = this.itemForceUpdate + 1
      },        

      // SELECT VOUCHER 
      searchVoucherBySupplier() {
        if(!this.crud.form.id) {
          if(this.crud.form.suppliers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherBySupplier({
              'suppliers_id': this.crud.form.suppliers_id,
              'reference': this.crud.form.selectTypeVoucher,
              'point_sale_id': this.crud.form.points_sales_id,
            });
            result.then((response) => {    
              this.crud.form.types_vouchers_id = response.data[0].id							
              this.searchVoucher()                          
            })
            .catch(error => {
              this.$awn.alert(Error.showError(error));
            })   						           
          }
        } else {                                      
          if( this.crud.form.types_vouchers ) {                          
            this.modal.form.title = this.crud.form.types_vouchers.name
            this.modal.form.title = this.modal.form.title + ' ' + this.crud.form.point_sales.point_sale.toString().padStart(4,'0')
            this.modal.form.title = this.modal.form.title + '-' + this.crud.form.number.toString().padStart(8,'0')
          }                   
        }
      },			
      searchVoucher() {
        if(!this.crud.form.id) {
          if(this.crud.form.suppliers_id && this.crud.form.points_sales_id ) {
            var result = serviceAPI.obtenerVoucherById({
              'points_sales_id': this.crud.form.points_sales_id,
              'types_vouchers_id' : this.crud.form.types_vouchers_id,
            });
            result.then((response) => {                   
              this.crud.form.types_vouchers = response.data.type_voucher
              this.crud.form.letter = response.data.type_voucher.letter                    
              this.crud.form.number = response.data.number
          
              if( this.crud.form.types_vouchers ) {              
                this.modal.form.title = this.crud.form.types_vouchers.name
                this.modal.form.title = this.modal.form.title + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
                this.modal.form.title = this.modal.form.title + '-' + this.crud.form.number.toString().padStart(8,'0')
              }                                
            })
          }
        } else {                                      
          if( this.crud.form.types_vouchers ) {                          
            this.modal.form.title = this.crud.form.types_vouchers.name          
            this.modal.form.title = this.modal.form.title + ' ' + this.crud.form.points_sales.point_sale.toString().padStart(4,'0')
            this.modal.form.title = this.modal.form.title + '-' + this.crud.form.number.toString().padStart(8,'0')
          }                   
        }
      },  

      // FILTER
      filterLoad() {   
        this.filterLoadSuppliers()          
        this.filterLoadPointSale()                
        this.filters.date_start = moment().subtract(30, 'days').format('YYYY-MM-DD') 
        this.filters.date_end = moment().format('YYYY-MM-DD') 
      },
      filterLoadSuppliers() {
        var result = serviceAPI.obtenerProveedores()        
        result.then((response) => {
          var data = response.data    

          this.arr.filters.suppliers = []
          data.forEach(element => {            
            if(element.code) {
              this.arr.filters.suppliers.push({ code: element.id, label: element.code + ' | ' + element.name })            
            } else {
              this.arr.filters.suppliers.push({ code: element.id, label: element.name })            
            }            
          });          
        })   
      },
      filterLoadPointSale() {
        var result = serviceAPI.obtenerPuntosVentas()        
        result.then((response) => {
          var data = response.data    

          this.arr.filters.points_sales = []
          data.forEach(element => {                            
            this.arr.filters.points_sales.push({ code: element.id, label: element.full_identification })            
          });          
        })   
      },       
      filterPayments(forceOpenSub=false) {
        this.table.isBusy = true
        var result = serviceAPI.filtrarPagos(this.filters, this.table.currentPage)        
        result.then((response) => {          
          var data = response.data
          this.table.tablePaginate = data

          this.table.items = data.data
          this.arr.payments = data.data
          this.prepareExport(data.data)
          
          this.table.items.forEach(element => {
            if(element.amount_pending>0) {
              element._rowVariant = 'warning'                          
            }
          })  

          // Refresca el drawer preseleccionado con los datos ya actualizados
          // (mismo item, recién recargado) — cualquier acción que modifique
          // el registro (imputar, desimputar, etc.) tiene que pasar
          // forceOpenSub=true para que esto corra. Guard de itemSelected: la
          // acción puede dispararse con el drawer cerrado (ej. Imputar desde
          // el kebab de la fila, no desde el propio drawer).
          this.table.items.forEach(element => {
            if(forceOpenSub && this.itemSelected && element.id == this.itemSelected.id) {
              this.openSub(element)
            }
          });

          if(this.table.rowSelected!=null) {            
            this.table.items[this.table.rowSelected]._showDetails = true                   
          }         

          if(this.$refs.table) {            
            this.$refs.table.$forceUpdate()
          }          
            
          this.table.isBusy = false
        })           
        .catch(error => {            
          this.table.isBusy = false  
          this.$awn.alert(ErrorToken.valid(error));
        })  
      },    

      // COMPROBANTE DETAIL
      openSub(item) {
        this.itemSelected = item

        this.tableSubPayment.items = item.details_payments
        this.tableSubImputation.items = item.details_imputations
        this.detailDrawer = true
      },
      hideSub() {
        this.tableSubPayment.items=[]
        this.tableSubImputation.items=[]
        this.itemSelected = null
        this.detailDrawer = false
      },
      // Anterior/siguiente desde el drawer: reusa openSub() con el vecino,
      // así el contenido y el resaltado de fila se actualizan por el mismo
      // camino que un click directo en la tabla.
      goPrevPayment() {
        if(!this.canGoPrevPayment) return

        var item = this.table.items[this.currentPaymentIndex - 1]
        if(item) this.openSub(item)
      },
      goNextPayment() {
        if(!this.canGoNextPayment) return

        var item = this.table.items[this.currentPaymentIndex + 1]
        if(item) this.openSub(item)
      },
      openDetail() {
        this.tableDetailPayment.items = this.itemSelected.details_payments
        this.tableDetailImputation.items = this.itemSelected.details_imputations

        if(this.itemSelected.accounting_entries){
          if(this.itemSelected.accounting_entries.details){
            this.tableAccounting.items = this.itemSelected.accounting_entries.details
          } else {
            this.tableAccounting.items = []
          }
        } else {
          this.tableAccounting.items = []
        }

        this.modal.detail._id = Math.random().toString(36).slice(2, 8)
        this.modal.detail.title = "Detalle del Pago"
        this.modal.detail.active = true
      },       
      calcPendingVoucher(item) {
        return parseFloat(item.purchase.amount_total) - parseFloat(item.purchase.amount_impute)
      },      

      // PRINT
      sendPrinterSale(item) {        
        this.crud.print.key=Math.random()
        this.crud.print.id=item.id
        this.crud.print.reference = item.reportDefault
      },    
      
      /****************
       * CONTABILIDAD *
       * **************/      
      getContabilidad() {          
        this.contabilidad.render = false
        this.contabilidad.typeDebe = ""
        this.contabilidad.typeHaber = "",
        this.contabilidad.defaultDebe = []
        this.contabilidad.defaultHaber = []
        this.contabilidad.reference = this.getRefVoucher
        this.contabilidad.amountTotal = parseFloat(this.crud.form.payment.total)
        
        // ASIENTO POR DEFECTO         
        this.contabilidad.defaultDebe = this.getContabilidadAsientoPago('debe')
        this.contabilidad.defaultHaber = this.getContabilidadAsientoPago('haber')        
        // END
        
        this.contabilidad.render = true
        this.forceUpdate()
      },

      getAsiento(object) {
        this.crud.form.accountingEntry = object
      },
      forceUpdate() {
        this.itemAccountingForceUpdate = this.itemAccountingForceUpdate + 1
      },

      getContabilidadAsientoPago(type) {   
        if(type == 'debe') {                  
          var arrDebe = [
            {
              account: this.crud.form.suppliers.accounting_account_default,
              amount: parseFloat(this.crud.form.payment.total),
              disabled: true,
            }                  
          ]

          return arrDebe
        }        

        if(type == 'haber') {        
          var arrHaber = []            

          if(this.crud.form.payment.cash.detail) {
            this.crud.form.payment.cash.detail.forEach(element => {
              arrHaber.push({
                account: element.cash.accounting_account_default,
                amount: parseFloat(element.total),
                disabled: true,
              })
            });            
          }

          if(this.crud.form.payment.cheques.detail) {
            this.crud.form.payment.cheques.detail.forEach(element => {
              arrHaber.push({
                account: element.accounting_account_default,
                amount: parseFloat(element.total),
                disabled: true,
              })
            });
          }

          return arrHaber
        }        
      },          
    }
  }
</script>
<style>
  .table-payments-sub {
    overflow: auto;
    max-height: 350px;
  }
  .table-full-detail-payments {
    overflow: auto;
    max-height: 350px;
  }
  .crud-payments-wizard .wizard-header {
    padding: 0px;
  }
  .crud-payments-item-input {
    height: 25px;
    text-align: center;
    font-weight: bold;
    font-size: 16px;
  }
  .crud-payments-table-items {
    height: 400px;
    overflow: auto;
  }
  .payments-payments-add-total {
    font-weight: 800 !important;
    font-size: 15px !important;
    text-align: right;
  }
  .payments-payments-add-total-final {
    font-weight: 800 !important;
    font-size: 15px !important;
    text-align: right;
  }
  .payments-imputation-total-align {
    margin-top: 25px;
  }
</style>
<style scoped>
  .b-avatar {
    width: 25px;
    height: 25px;
  }
</style>
